import React, { useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { defaultRoutes } from '../../router/routes';

export default function LoginPage() {
  const { supabase, setUser, setUserProfile, user, userProfile, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const navigate = useNavigate();

  // Redirect if user is already logged in
  useEffect(() => {
    if (!loading && user && userProfile) {
      const defaultSection = defaultRoutes[userProfile.role] || 'dialer';
      navigate(`/app/${user.id}/${defaultSection}`, { replace: true });
    }
  }, [user, userProfile, loading, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitLoading(true);
    const { data, error: loginError } = await supabase.auth.signInWithPassword({ email, password });
    const user = data?.user;
    if (loginError) {
      setError(loginError.message === 'Invalid login credentials' ? 'Incorrect password' : loginError.message);
      setSubmitLoading(false);
      return;
    }
    if (user) {
      setUser(user);
      // Fetch user profile
      const { data: profileData, error: profileError } = await supabase
        .from('users')
        .select('email, full_name, twilio_phone_number, role')
        .eq('id', user.id)
        .single();
      if (!profileError) {
        setUserProfile(profileData);
        const defaultSection = defaultRoutes[profileData.role] || 'dialer';
        navigate(`/app/${user.id}/${defaultSection}`);
      } else {
        navigate(`/app/${user.id}/dialer`);
      }
    } else {
      setError('Login failed.');
    }
    setSubmitLoading(false);
  };

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
        <div style={{ fontSize: '16px', color: '#888' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
      <form onSubmit={handleLogin} style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px #0001', padding: 40, width: 400, maxWidth: '90vw', display: 'flex', flexDirection: 'column', gap: 24 }}>
        <img 
          src="https://amicredit.ro/wp-content/uploads/2024/02/amicredit.svg" 
          alt="AMI Credit" 
          style={{ width: 180, margin: '0 auto 12px auto' }} 
          onError={(e) => {
            // Fallback if logo image doesn't exist
            e.target.style.display = 'none';
            // Show fallback text
            const fallback = document.createElement('div');
            fallback.innerHTML = '<span style="color: #00d084; font-weight: 700; font-size: 24px;">Ami</span><span style="color: #374151; font-weight: 700; font-size: 24px;">Credit</span>';
            fallback.style.textAlign = 'center';
            fallback.style.margin = '0 auto 12px auto';
            e.target.parentNode.insertBefore(fallback, e.target);
          }}
        />
        <h2 style={{ margin: 0, fontWeight: 700 }}>Welcome back</h2>
        <p style={{ margin: 0, color: '#555' }}>Please enter your account credentials.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ fontWeight: 500 }}>Email</label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="Enter your email"
            style={{ padding: 10, borderRadius: 6, border: error && !email.match(/^[^@]+@[^@]+\.[^@]+$/) ? '1.5px solid #e74c3c' : '1.5px solid #eee', outline: 'none', fontSize: 16 }}
            required
          />
          {error && !email.match(/^[^@]+@[^@]+\.[^@]+$/) && <span style={{ color: '#e74c3c', fontSize: 13 }}>Invalid email format</span>}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ fontWeight: 500 }}>Password</label>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Enter your password"
            style={{ padding: 10, borderRadius: 6, border: error && password ? '1.5px solid #e74c3c' : '1.5px solid #eee', outline: 'none', fontSize: 16 }}
            required
          />
          {error && password && <span style={{ color: '#e74c3c', fontSize: 13 }}>{error}</span>}
        </div>
        <div style={{ marginBottom: 8 }}>
          <Link to="/forgot-password" style={{ color: '#e65c00', fontWeight: 500, fontSize: 15, textDecoration: 'none' }}>Forgot Password?</Link>
        </div>
        <button type="submit" disabled={submitLoading} style={{ background: '#e65c00', color: '#fff', border: 'none', borderRadius: 6, padding: '12px 0', fontSize: 17, fontWeight: 600, cursor: 'pointer', marginBottom: 8, opacity: submitLoading ? 0.7 : 1 }}>
          {submitLoading ? 'Logging in...' : 'Login'}
        </button>
        <div style={{ textAlign: 'center', color: '#555', fontSize: 15 }}>
          Don&apos;t have an account?{' '}
          <Link to="/signup" style={{ color: '#222', fontWeight: 600 }}>Sign Up</Link>
        </div>
      </form>
    </div>
  );
} 